/**
 * @fileoverview Project recorded pipeline, stage, and agent runs into a shared activity vocabulary. `IntakeRecordTabs.js` and pipeline views consume these presentation records. Run identity, status, and timing retain their backend meaning; combining them for display does not create business events or replace the canonical lead timeline.
 */

import { getActivityDetail, getRunTime, getStageLabel, getTimestamp, titleize } from './intakeFormatters';
import { DEDUPE_REVIEW_TITLE, REVIEW_RUN_STATUSES } from './intakeStatuses';

// Builds the merged, newest-first feed of pipeline/stage/agent run activity shown in
// the pipeline overview and the Run Logs tab.



function isDedupeReviewRun(status, run = {}) {
  const stageKey = run?.stage_key || run?.agent_stage_key || '';
  return REVIEW_RUN_STATUSES.has(status) && (stageKey === 'ingest_dedupe' || (!stageKey && run?.trigger_type));
}



function getRunStatusLabel(run = {}) {
  return isDedupeReviewRun(run.status, run) ? DEDUPE_REVIEW_TITLE : titleize(run.status);
}



function getRunVerb(status, run = {}) {
	switch (status) {
    case 'pending':
      return 'queued';
    case 'queued':
      return 'queued';
    case 'waiting':
      return 'waiting';
    case 'completed':
    case 'approved':
    case 'accepted':
    case 'reviewed':
    case 'converted':
      return 'completed';
    case 'failed':
    case 'error':
      return 'needs attention';
    case 'running':
    case 'processing':
      return 'started';
    case 'stale':
      return 'stale';
    case 'skipped':
      return 'skipped';
    case 'needs_review':
    case 'review_required':
      if (isDedupeReviewRun(status, run)) return 'duplicate review required';
      return 'needs review';
    default:
      return titleize(status).toLowerCase();
  }
}



function getPipelineVersionLabel(run) {
  if (run?.pipeline_version_number) return `Pipeline v${run.pipeline_version_number}`;
  if (run?.pipeline_version_id) return `Pipeline #${run.pipeline_version_id}`;
  return null;
}



function getPromptVersionLabel(run) {
  if (run?.prompt_version_number) return `Prompt v${run.prompt_version_number}`;
  if (run?.prompt_version_id) return `Prompt #${run.prompt_version_id}`;
  return null;
}



export function buildActivityItems(pipelineRuns, stageRuns, agentRuns) {
  const pipelineItems = pipelineRuns.map((run) => ({
    id: `pipeline-${run.id}`,
    time: getRunTime(run),
    fallbackOrder: run.id || 0,
    label: `Pipeline run ${getRunVerb(run.status, run)}`,
    detail: getActivityDetail([getPipelineVersionLabel(run), run.trigger_type || 'manual', run.triggered_by || 'system', run.error]),
    status: run.status,
    statusLabel: getRunStatusLabel(run)
  }));

  const stageItems = stageRuns.map((run) => ({
    id: `stage-${run.id}`,
    time: getRunTime(run),
    fallbackOrder: run.id || 0,
    label: `${run.stage_label || getStageLabel(run.stage_key)} ${getRunVerb(run.status, run)}`,
    detail: run.error || getActivityDetail([getPipelineVersionLabel(run), 'Stage activity']),
    status: run.status,
    statusLabel: getRunStatusLabel(run)
  }));

  const agentItems = agentRuns.map((run) => ({
    id: `agent-${run.id}`,
    kind: 'agent',
    runId: run.id,
    leadId: run.lead_id,
    agent_id: run.agent_id,
    agent_display_name: run.agent_display_name,
    agent_key: run.agent_key,
    time: getRunTime(run),
    fallbackOrder: run.id || 0,
    label: `${run.agent_display_name || run.agent_key || `Agent #${run.agent_id || '-'}`} ${getRunVerb(run.status, run)}`,
    detail: getActivityDetail([
    getPipelineVersionLabel(run),
    getPromptVersionLabel(run),
    run.provider,
    run.model_tier,
    run.model,
    run.total_tokens ? `${run.total_tokens} tokens` : null,
    run.error]
    ),
    status: run.status,
    statusLabel: getRunStatusLabel(run)
  }));

  return pipelineItems
  .concat(stageItems, agentItems)
  .sort((left, right) => {
    const timeDiff = getTimestamp(right.time) - getTimestamp(left.time);
    return timeDiff || right.fallbackOrder - left.fallbackOrder;
  });
}
